import { Box, FormControl, Grid, MenuItem, Select, Typography, Button, Chip, OutlinedInput, InputLabel } from '@mui/material'
import { useEffect, useState } from 'react';
import { ApiFetch } from '../../../Utils/ApiFetch';
import Loading from '../../../Components/Generic/Loading';
import { translateFunc } from '../../../Helpers/translateFunc';
import { ErrorOutline } from '@material-ui/icons';

export const Filters = ({t, isAuthorized, appliedFilters, setAppliedFilters, globalContext, selectedAsset, selectedBox}) => {
    const [loadingFilters, setLoadingFilters] = useState(false)
    const [selectedValues, setSelectedValues] = useState({});
    const [filters, setFilters] = useState([])
    const [errorLoadFilter, setErrorLoadFilter] = useState(null)
    const [filterExist, setFilterExist] = useState(false)
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    useEffect(() => {
        if(!isAuthorized || !filterExist) return
        
        setLoadingFilters(true)
        ApiFetch(
            `controlPanel/filters/${selectedAsset.id}/${selectedBox.id}`,
            globalContext,
            true,
            async (result) => {
              if (result.status === 200) {
                const res = await result.json();

                setFilters(res)
                setLoadingFilters(false)
              } else {
                setLoadingFilters(false)
              }
            },
            (error) => {
                error.json().then(err => {setErrorLoadFilter(err)})
                setLoadingFilters(false)
                setFilters([])
            },
            {
              method: 'GET',
            }
        );
    }, [isAuthorized, filterExist, selectedAsset.id, selectedBox.id])

    useEffect(() => {
        const filters = globalContext.getSessionDataByKey('models')?.[selectedAsset.id]?.controlPanel?.filter;
        
        setFilterExist((filters && filters.length > 0) ? true : false)
    }, [selectedAsset.id])
           
    const resetChildren = (parentName, values) => {
        filters.forEach(f => {
            if (f.fk === parentName) {
                delete values[f.name]
                resetChildren(f.name, values);
            }
        });
    };
    
    const handleSelectChange = (attName, attLabel, attType, value, attValues) => {
        const newValues = { ...selectedValues }
        const condition = Array.isArray(value) ? 
            value.length === 0 : 
            value === ""

        if (condition) {
            delete newValues[attName];
        } else {
            let rawValue = value
            if (attValues && attValues.values) {
                const found = attValues?.filter(v => value.includes(v.key))?.map(v => ({key: v.key, label: v.label}));
                rawValue = found
            }

            newValues[attName] = {
                fieldLabel: attLabel,
                type: attType,
                value: rawValue
            };
        }
            
        resetChildren(attName, newValues);
        setSelectedValues(newValues);
    };

    const removeFilterByKey= (key) => {
        const newValues = { ...appliedFilters }
        delete newValues[key];

        resetChildren(key, newValues);
        setSelectedValues(newValues)
        setAppliedFilters(newValues);
    }

    const handleApplyFilters = () => {
        setAppliedFilters(selectedValues);
    };

    const renderFilter = (attribute, index) => {
        switch (attribute.type) {
            case 'select':
            case 'multiselect': {
                const isMulti = attribute.type === 'multiselect';
                const labelId = `select-label-${attribute.name}-${index}`;
                const labelText = translateFunc(t, `basicFormStepByStep.placeholdersTranslate.${attribute.label}`, attribute.label);

                const selectedValue = isMulti
                    ? selectedValues[attribute.name]?.value?.map(v => v.key) ?? []
                    : selectedValues[attribute.name]?.value ?? "";

                const filteredValues = attribute.fk
                    ? attribute.values.filter(val => {
                        const fkVal = selectedValues[attribute.fk]?.value;
                        if (isMulti) return fkVal?.map(v => v.key).includes(val.fk);
                        return fkVal === val.fk;
                    })
                    : attribute.values;
                return (
                    <FormControl fullWidth size="small">
                        <InputLabel size="small" id={labelId}>
                            {labelText}
                        </InputLabel>
                        <Select
                            name={attribute.name}
                            labelId={labelId}
                            id={labelId}
                            label={labelText}
                            size="small"
                            multiple={isMulti}
                            value={selectedValue}
                            onChange={(val) =>
                                handleSelectChange(
                                    attribute.name,
                                    attribute.label,
                                    attribute.type,
                                    val.target.value,
                                    isMulti ? attribute.values : undefined
                                )
                            }
                            input={
                                isMulti ? (
                                    <OutlinedInput id="select-multiple-chip" label={labelText} />
                                ) : undefined
                            }
                            renderValue={
                                isMulti
                                    ? (selectedKeys) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selectedKeys.map((key) => {
                                                const item = attribute.values.find((v) => v.key === key);
                                                return (
                                                    <Chip
                                                        size="small"
                                                        key={key}
                                                        label={translateFunc(
                                                            t,
                                                            `basicFormStepByStep.fieldTranslate.select.${attribute.name}.${item.key}`,
                                                            item.label
                                                        )}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )
                                    : undefined
                            }
                            MenuProps={MenuProps}
                            sx={isMulti ? { width: '100%', mr: 2 } : {}}
                        >
                            {!isMulti && (
                                <MenuItem value="">
                                    <em>{t(`miscellaneous.deselect`)}</em>
                                </MenuItem>
                            )}
                            {isMulti && filteredValues.length === 0 && (
                                <MenuItem disabled value="">
                                    <em>{t(`miscellaneous.withoutOptions`)}</em>
                                </MenuItem>
                            )}
                            {filteredValues.map(val => (
                                <MenuItem key={`item-${val.key}-${index}`} value={val.key}>
                                    {translateFunc(
                                        t,
                                        `basicFormStepByStep.fieldTranslate.select.${attribute.name}.${val.key}`,
                                        val.label
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            }
            default:
                break;
        }
    }

    return (filterExist &&
        <Box sx={{ p: 2, boxShadow: "1" }}>
            <Grid container spacing={2}>   
                {loadingFilters ? (
                    <Box 
                        sx={{ 
                            width: '100%',
                            justifyContent: 'center',
                            mt: 4
                        }}
                    >
                        <Loading height={50} size={50}/>
                    </Box>
                ) : filters?.length > 0 ? <>
                    {filters.map((filter, index) => (
                        <Grid item xs={12} md={6} lg={3} xl={2} key={index}>
                            {renderFilter(filter, index)}
                        </Grid>
                    ))}
                    <Grid item component={FormControl} sx={{ pr: 2, justifyContent: 'flex-end' }} xs={12} >
                        <Button disabled={loadingFilters} variant="outlined" onClick={handleApplyFilters}>
                            {t("filter.button.apply")}
                        </Button>
                    </Grid>
                </> : <>
                    {errorLoadFilter && 
                        <Box sx={{ pb: 2, width: '100%', justifyContent: 'center', mt: 4}}>
                            <Typography className='fw-bold text-danger' textAlign="center" >
                                <ErrorOutline sx={{ color: "#e91e63" }} fontSize='medium'/> {translateFunc(t, `controlPanel.error.${errorLoadFilter}`, errorLoadFilter)}
                            </Typography>
                        </Box>
                    }
                </>}
                {appliedFilters && Object.keys(appliedFilters).length > 0 && <Grid item xs={12} key="filters-selected">
                    <Box display="flex" flexWrap="wrap"  style={{ borderTop: '1px solid #ddd', paddingTop: 20}}>
                        {Object.keys(appliedFilters).map(filterKey => (
                            <Chip
                                key={filterKey}
                                label={`
                                    ${translateFunc(t, `basicFormStepByStep.placeholdersTranslate.${appliedFilters[filterKey]?.fieldLabel}`, appliedFilters[filterKey]?.fieldLabel)}:
                                    ${(Array.isArray(appliedFilters[filterKey]?.value) ?
                                        appliedFilters[filterKey]?.value.map(v => 
                                            translateFunc(
                                                t, 
                                                `basicFormStepByStep.fieldTranslate.${appliedFilters[filterKey]?.type === 'multiselect' ? 'select' : appliedFilters[filterKey]?.type}.${filterKey}.${v.key}`, 
                                                v.label
                                            )
                                        )?.join(', ')
                                        :
                                        translateFunc(
                                            t, 
                                            `basicFormStepByStep.fieldTranslate.${appliedFilters[filterKey]?.type}.${filterKey}.${appliedFilters[filterKey]?.value}`, 
                                            appliedFilters[filterKey]?.value
                                        )
                                    )}
                                `}
                                color="secondary"
                                sx={{ mr: 1, mb: 1 }}
                                onDelete={() => removeFilterByKey(filterKey)}
                            />
                        ))}
                    </Box>
                </Grid>}
            </Grid>
        </Box>
    )
}